import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashPickBy from 'lodash/fp/pickBy';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import { SCREEN_ID_PARTNER_SETTINGS_APPROVAL_FLOW } from 'client/constants/screenIds';

import { SR_API_ACTION_ADMIN_UPDATE_ITEM } from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withLocationQuery from 'client/helpers/withLocationQuery';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';

import setNullEmptyValues from 'common/util/setNullEmptyValues';
import PartnerActions from 'client/actions/Partner';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrPartnerApprovalFlowSettingsForm from 'client/components/SrPartnerApprovalFlowSettingsForm/SrPartnerApprovalFlowSettingsForm';
import { partnerBillingFieldGroup } from 'server/models/Partner/fieldgroups';

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS_APPROVAL_FLOW)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
}))
@withApiState(({ match }) => ({
  adminUpdateItemApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
    queryId: `partnerExperienceConfiguration-${match.params.partnerSlug}`,
  },
}))
@withLocationQuery
class PartnerSettingsApprovalFlowPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
    section: 'Partner',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const partner = inject.state.get(['partners', 'bySlug', partnerSlug]);

    const promises = [];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises);
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    isTransitioning: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    adminUpdateItemApiState: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      shouldShowSavedModal: false,
    };
  }

  _handleReload = () => {
    const { partner, dispatch } = this.props;
    dispatch(PartnerActions.loadPartnerBySlug, {
      slug: partner.slug,
      forceRefresh: true,
    });
    this.setState({
      shouldShowSavedModal: true,
    });
    setTimeout(() => {
      this.setState({
        shouldShowSavedModal: false,
      });
    }, 1000);
  };

  _updatePartnerExperienceConfiguration = (values) => {
    const { partner, dispatch } = this.props;
    const filteredValues = lodashPickBy((_, key) => {
      return !partnerBillingFieldGroup.includes(key);
    }, values);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: `partnerExperienceConfiguration-${partner.id}`,
      payload: {
        table: 'partnerExperienceConfiguration',
        where: {
          partnerId: partner.id,
        },
        updates: filteredValues,
      },
    });
  };

  _updatePartnerBillingInfo = (values) => {
    const { partner, dispatch } = this.props;
    const filteredValues = setNullEmptyValues(
      lodashPickBy((_, key) => {
        return partnerBillingFieldGroup.includes(key);
      }, values),
    );

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: `partner-billing-${partner.id}`,
      payload: {
        table: 'partners',
        where: {
          id: partner.id,
        },
        updates: filteredValues,
      },
    });
  };

  _onSubmit = (values) => {
    Promise.all([
      this._updatePartnerExperienceConfiguration(values),
      this._updatePartnerBillingInfo(values),
    ]).then(() => this._handleReload());
  };

  render() {
    const { partner, currentUser, isTransitioning, adminUpdateItemApiState } = this.props;

    const { shouldShowSavedModal } = this.state;

    if (isTransitioning && !partner) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/settings`}
        navbarTitle={partner.displayName}
        pageHeader="Approval Flow"
      >
        <SrPartnerApprovalFlowSettingsForm
          partner={partner}
          onSubmit={this._onSubmit}
          inProgress={adminUpdateItemApiState.inProgress}
        />
        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerSettingsApprovalFlowPage.displayName = 'PartnerSettingsApprovalFlowPage';

export default PartnerSettingsApprovalFlowPage;
